import { useMemo, useState } from 'react'
import type { FC } from 'react'
import {
	Text,
	Flex,
	Button,
	Skeleton,
	Box,
	Heading,
	Grid,
	Tooltip,
	useBreakpointValue,
	Select,
	Link as ChakraLink,
} from '@chakra-ui/react'
import type { ChakraProps } from '@chakra-ui/react'
import {
	Feature,
	InformationCircleBoldIcn,
	ContentSectionHeading,
} from '@alexanderathoodly/ui-library'
import { useOfficesDisplay, OFFER_TYPE_DISPLAY } from './utils'
import {
	formatInt,
	flexOfficeOffersByFlexOffice,
	useYtaQuery,
} from '@alexanderathoodly/data-models'
import type {
	FlexOfficeResult,
	FlexOfficeOfferResult,
} from '@alexanderathoodly/data-models'
import { PLACEHOLDER_SRC, SIZE_290 } from '@yta/image-proxy'
import { sendGA4Event } from '~/analytics/ga'
import { RequestShowingButton } from '~/forms/showingrequestform'
import { useQuery } from 'react-query'
import { ImgProxy } from '~/utils/imgproxy'
import { useUser } from 'reactfire'
import {
	ShowPriceButton,
	ShowPriceModal,
	useShowPriceModal,
} from '~/forms/showpriceform'
import { useTranslation } from 'react-i18next'
import { useLocationPage } from './context'
import { getProviderQuery } from './queries'
import { useLocation, Link, useHistory } from 'react-router-dom'

export const InfoAboutBlurredPrice: FC = () => {
	const { t } = useTranslation()
	return (
		<Flex gap={2} alignItems="center">
			<InformationCircleBoldIcn
				backgroundColor="white"
				borderRadius="full"
				h={4}
				w={4}
				sx={{ path: { fill: 'blue.500' }, stroke: { fill: 'white' } }}
			/>
			<Text fontSize="xs">
				{t(
					'På yta.se kan du alltid se precis vad kontoret kostar - skapa ett kostnadsfritt konto för tillgång till samtliga priser'
				)}
			</Text>
		</Flex>
	)
}

const OfficeList: FC = ({ ...rest }) => {
	const { offices, currentOffice } = useOfficesDisplay()
	const otherOffices =
		offices && currentOffice
			? offices?.slice().filter((o) => o.id != currentOffice.id)
			: []
	return (
		<Flex {...rest} mt={1} direction="column">
			{otherOffices.map((office, i) => (
				<OfficeListItem
					key={`${office.id}`}
					office={office}
					border={otherOffices.length == i + 1 ? 'none' : ''}
				/>
			))}
		</Flex>
	)
}

const CollapsableText: FC<
	{
		text: string | undefined
		maxCharCount: number
	} & ChakraProps
> = ({ text, maxCharCount, ...rest }) => {
	const { t } = useTranslation()
	const [isFullTextShowing, setIsFullTextShowing] = useState(
		text && text?.length <= maxCharCount
	)
	if (!text) {
		return null
	}

	const truncatedText = `${text.slice(0, maxCharCount)}...`

	return (
		<Flex direction="column">
			<Text whiteSpace="pre-wrap" color="grey.800" {...rest}>
				{isFullTextShowing ? text : truncatedText}
			</Text>
			{text.length > maxCharCount && (
				<Button
					variant="link"
					size="sm"
					ml="auto"
					onClick={() => setIsFullTextShowing((oldVal) => !oldVal)}
				>
					{isFullTextShowing ? t('Visa mindre') : t('Visa mer')}
				</Button>
			)}
		</Flex>
	)
}
const OfficeListItem: FC<
	{
		office: FlexOfficeResult
	} & ChakraProps
> = ({ office, ...rest }) => {
	const isToolTipDisabled = useBreakpointValue(
		{ base: true, md: false },
		{ ssr: true }
	)
	const { i18n, t } = useTranslation()
	const { location, currentOffice } = useOfficesDisplay()
	const { data: user } = useUser()
	const { data: offers } = useYtaQuery(
		flexOfficeOffersByFlexOffice({ flex_office: office?.id || '' })
	)
	const routerLocation = useLocation()
	const cheapestOffer = useMemo(() => {
		return offers?.sort((a, b) => {
			return a.price - b.price
		})[0]?.price
	}, [offers])
	const officeDescription =
		i18n.language != 'en' ? office.description : office.description_en
	const { onShowPriceModalOpen } = useShowPriceModal(
		'flex office',
		currentOffice?.id || ''
	)
	if (!location || !office) {
		return <></>
	}

	return (
		<Flex
			as="article"
			direction="column"
			borderColor="grey.100"
			borderStyle="solid"
			borderBottomWidth="thin"
			py={7}
			gap={3}
			{...rest}
		>
			<Heading as="h3" fontSize="md" fontWeight="semibold">
				{`${location.address.street} ${location.address.locator} - ${office.size}m²`}
			</Heading>
			<Flex direction={{ base: 'column', md: 'row' }}>
				<Box position="relative" mr={{ md: 6 }}>
					<ImgProxy
						sizeSet={{ base: SIZE_290 }}
						src={office.images[0] || PLACEHOLDER_SRC}
						loading="lazy"
						height={{ lg: 32 }}
						width={{ lg: 56 }}
						minWidth={{ lg: 56 }}
						alt={
							office.images[0]
								? `
							Eget kontor på ${location.name}`
								: `ingen tillgänglig bild`
						}
					/>
				</Box>
				<Flex direction="column">
					<Grid
						gap={4}
						templateRows="repeat(2,1fr)"
						templateColumns="repeat(3,1fr)"
					>
						<Feature
							name={t('Arbetsplatser')}
							value={office.max_workspaces.toString()}
						/>
						<Feature
							name={t('Storlek')}
							value={`${formatInt(office.size)}m²`}
						/>
						{user ? (
							<Feature
								gridColumn="span 2"
								name="Hyra/mån"
								value={
									cheapestOffer
										? `${formatInt(cheapestOffer)} ${t('kr / mån')}`
										: t('Saknas')
								}
							/>
						) : (
							<Flex gap="4px" gridColumn="span 2">
								<Text as="dt" color="grey.500">
									{t('Kostnad')}:
								</Text>
								<Tooltip
									label={<InfoAboutBlurredPrice />}
									maxW="max-content"
									borderRadius="5px"
									py={1}
									isDisabled={isToolTipDisabled || false}
								>
									<Flex gap={1}>
										<Text
											as="dd"
											fontWeight="semibold"
											color="grey.800"
											filter="blur(4px)"
											_hover={{ cursor: 'pointer' }}
											onClick={() => {
												onShowPriceModalOpen()
												sendGA4Event('initiate_price_account', {
													step: 'initiate',
													location: 'blurred price',
												})
											}}
										>
											12345
										</Text>
										<Text>{t('kr / mån')}</Text>
									</Flex>
								</Tooltip>
							</Flex>
						)}
						<Box
							display={{ base: 'none', md: 'flex' }}
							ml="auto"
							gridRowStart="1"
							gridRowEnd="3"
							gridColumn="3"
						>
							{office.id === currentOffice?.id ? (
								<Button
									variant="link"
									textDecoration="underline"
									color="blue.500"
									size="sm"
									width="min-content"
									px={3}
								>
									{t('Visas')}
								</Button>
							) : (
								<Button
									as={Link}
									to={`${t('/kontor', { ns: 'url' })}/${location.url}/${
										office.id
									}${routerLocation.search}`}
									replace={true}
									variant="outline"
									width="min-content"
								>
									{t('Visa')}
								</Button>
							)}
						</Box>
					</Grid>
					<Skeleton isLoaded={!!office.description}>
						<Text whiteSpace="pre-wrap" color="grey.800" mt={6} noOfLines={3}>
							{officeDescription}
						</Text>
					</Skeleton>
				</Flex>
			</Flex>
			<Box display={{ base: 'flex', md: 'none' }} mt={6}>
				{office.id === currentOffice?.id ? (
					<Button
						variant="link"
						textDecoration="underline"
						color="blue.500"
						size="sm"
						width="100%"
					>
						{t('Visas')}
					</Button>
				) : (
					<Button
						as={Link}
						to={`${t('/kontor', { ns: 'url' })}/${location.url}/${office.id}${
							routerLocation.search
						}`}
						replace={true}
						variant="outline"
						width="min-content"
					>
						{t('Visa')}
					</Button>
				)}
			</Box>
		</Flex>
	)
}

export const OfficesDisplay: FC = () => {
	const { isError, error } = useOfficesDisplay()

	if (isError) {
		return <span>Error: {(error as Error).message}</span>
	}

	return (
		<Flex display="flex" flexDirection="column">
			<OfficeList />
		</Flex>
	)
}

export const OfficeSwitcher: FC<ChakraProps> = ({ ...rest }) => {
	const { currentOffice, offices } = useOfficesDisplay()
	const { location } = useLocationPage()
	const history = useHistory()
	const routerLocation = useLocation()
	const { t } = useTranslation()

	if (offices && offices?.length < 2) {
		return null
	}

	return currentOffice ? (
		<Select
			placeholder={`${currentOffice?.size}m²`}
			value={currentOffice?.id}
			maxW="fit-content"
			variant="outline"
			bg="white"
			color="blue.500"
			fontWeight="semibold"
			onChange={(e) => {
				if (!location) {
					return
				}
				const newOffice = offices?.slice().find((o) => o.id === e.target.value)
				if (newOffice) {
					history.replace(
						`${t('/kontor', { ns: 'url' })}/${location?.url}/${newOffice.id}${
							routerLocation.search
						}`
					)
				}
			}}
			_hover={{ cursor: 'pointer' }}
			{...rest}
		>
			{offices
				?.slice()
				.filter((o) => o.id != currentOffice?.id)
				.map((office) => (
					<option key={`select-office-${office.id}`} value={office.id}>
						{`${office.size}m²`}
					</option>
				))}
		</Select>
	) : null
}

const OfferListItem: FC<{ offer: FlexOfficeOfferResult } & ChakraProps> = ({
	offer,
	...rest
}) => {
	const { i18n, t } = useTranslation()
	const { currentOffice } = useOfficesDisplay()
	const { location } = useLocationPage()
	const { data: provider } = useQuery(
		getProviderQuery(location?.provider || '')
	)
	const { data: user } = useUser()
	const isToolTipDisabled = useBreakpointValue(
		{ base: true, md: false },
		{ ssr: true }
	)
	const { onShowPriceModalOpen } = useShowPriceModal(
		'flex office',
		currentOffice?.id || ''
	)
	const offerName = i18n.language != 'en' ? offer.name : offer.name_en
	const availability =
		currentOffice?.availability &&
		new Date(currentOffice.availability) > new Date()
			? new Date(currentOffice.availability).toLocaleDateString()
			: currentOffice?.vacant
			? t('Tillgänglig')
			: null
	const providerNameWithS = provider
		? provider.name.slice(-1) == 's'
			? `${provider.name}'`
			: `${provider.name}'s`
		: ''
	const offerDescription =
		i18n.language != 'en' ? offer.description : offer.description_en
	return (
		<Flex direction="column" py={6} {...rest}>
			<Text fontWeight="semibold" color="gray.600" mb={4}>
				{offerName}
			</Text>
			<Flex gap={3} mb={3}>
				<Feature
					name={t('Avtalstyp')}
					value={t(OFFER_TYPE_DISPLAY[offer.type])}
				/>
				{user ? (
					<Feature
						name={t('Kostnad')}
						value={`${formatInt(offer.price)} ${t('kr / mån')}`}
					/>
				) : (
					<Flex gap="4px" gridColumn="span 2">
						<Text as="dt" color="grey.500">
							{t('Kostnad')}:
						</Text>
						<Tooltip
							label={<InfoAboutBlurredPrice />}
							maxW="max-content"
							borderRadius="5px"
							py={1}
							isDisabled={isToolTipDisabled || false}
						>
							<Flex gap={1}>
								<Text
									as="dd"
									fontWeight="semibold"
									color="grey.800"
									filter="blur(4px)"
									_hover={{ cursor: 'pointer' }}
									onClick={() => {
										onShowPriceModalOpen()
										sendGA4Event('initiate_price_account', {
											step: 'initiate',
											location: 'blurred price',
										})
									}}
								>
									12345
								</Text>
								<Text>{t('kr / mån')}</Text>
							</Flex>
						</Tooltip>
					</Flex>
				)}
			</Flex>
			<Flex gap={3} mb={4}>
				<Feature
					name={t('Avtalslängd')}
					value={t('Från {{contract_length}} månader', {
						contract_length: offer.contract_length,
					})}
				/>
				{availability != null && (
					<Feature name={t('Inflytt')} value={availability} />
				)}
			</Flex>
			<Text fontWeight="semibold" color="gray.600" mb={3}>
				{`${t('Om')} ${providerNameWithS} ${offerName}`}
			</Text>
			<CollapsableText text={offerDescription} maxCharCount={100} />
		</Flex>
	)
}

const OfferList: FC<{ offers: FlexOfficeOfferResult[] } & ChakraProps> = ({
	offers,
	...rest
}) => {
	const { t } = useTranslation()

	return (
		<Box {...rest}>
			<Text fontSize="md" fontWeight="semibold" mb={3}>
				{t('Erbjudna avtalsformer')}
			</Text>
			<Flex direction="column" gap={3}>
				{offers?.map((offer, i) => (
					<OfferListItem
						key={`offer-item-${offer.id}`}
						offer={offer}
						borderTop="1px"
						borderBottom={i + 1 == offers.length ? '1px' : 'none'}
						borderColor="grey.100"
					/>
				))}
			</Flex>
		</Box>
	)
}

export const DocumentItem: FC<{ src: string; text: string } & ChakraProps> = ({
	src,
	text,
	...rest
}) => {
	return (
		<ChakraLink href={src} target="_blank" {...rest}>
			<ImgProxy
				sizeSet={{ base: SIZE_290 }}
				src={src || PLACEHOLDER_SRC}
				loading="lazy"
				height={16}
				width="min-content"
				alt={'Eget kontor'}
				cursor="pointer"
			/>
			<Text fontWeight="semibold" color="gray.600" fontSize="xs">
				{text}
			</Text>
		</ChakraLink>
	)
}
export const CurrentOfficeDisplay: FC<ChakraProps> = ({ ...rest }) => {
	const { i18n, t } = useTranslation()

	const { location } = useLocationPage()
	const { currentOffice } = useOfficesDisplay()
	const { data: offers } = useYtaQuery(
		flexOfficeOffersByFlexOffice({ flex_office: currentOffice?.id || '' })
	)

	const { status, data: user } = useUser()

	const { isShowPriceModalOpen, onShowPriceModalClose, onShowPriceModalOpen } =
		useShowPriceModal('flex office', currentOffice?.id || '')

	const officeDescription =
		i18n.language != 'en'
			? currentOffice?.description
			: currentOffice?.description_en

	const shortestContractLength = useMemo(() => {
		return offers
			? offers
					?.slice()
					.map((o) => o.contract_length)
					.sort()[0]
			: undefined
	}, [offers])
	const cheapestOffer = useMemo(() => {
		return offers?.sort((a, b) => {
			return a.price - b.price
		})[0]?.price
	}, [offers])
	return currentOffice && location ? (
		<Box
			bg="white"
			borderRadius="base"
			height="min-content"
			border="1px"
			borderColor="grey.100"
			shadow="xl"
			py={7}
			px={6}
			mb={5}
			{...rest}
		>
			<Flex mb={4}>
				<ContentSectionHeading>
					{`${location.address.street} ${location.address.locator} • ${currentOffice.size}m²`}
				</ContentSectionHeading>
				{status == 'loading' ? (
					<Skeleton w="100%" h={10} borderRadius="md" />
				) : user && currentOffice ? (
					<RequestShowingButton
						flexoffice={currentOffice}
						variant="outline"
						ml="auto"
						width="min-content"
					/>
				) : (
					<ShowPriceButton
						onClick={onShowPriceModalOpen}
						GA4EventLocation="flex office"
						variant="solid"
						ml="auto"
					>
						{t('Se pris')}
					</ShowPriceButton>
				)}
				{currentOffice && location && (
					<ShowPriceModal
						location={location}
						flexOffice={{ ...currentOffice, fromPrice: cheapestOffer }}
						isOpen={isShowPriceModalOpen}
						onClose={onShowPriceModalClose}
					/>
				)}
			</Flex>
			<Flex gap={3} mb={3}>
				<Feature
					name={t('Arbetsplatser')}
					value={currentOffice.max_workspaces.toString()}
				/>
				<Feature
					name={t('Storlek')}
					value={`${formatInt(currentOffice.size)}m²`}
				/>
			</Flex>
			{shortestContractLength && (
				<Feature
					name={t('Avtalstid')}
					value={t('Från {{shortest_contract_length}} månader', {
						shortest_contract_length: shortestContractLength,
					})}
				/>
			)}
			<Box my={6}>
				<Text fontSize="md" fontWeight="semibold" mb={2}>
					{t('Om kontoret')}
				</Text>
				<CollapsableText text={officeDescription} maxCharCount={200} />
			</Box>
			{offers && <OfferList offers={offers} />}
			<Text
				id="floorplans-and-conditions"
				fontSize="md"
				fontWeight="semibold"
				my={2}
			>
				{t('Planritningar och villkor')}
			</Text>
			<Flex gap={3}>
				{currentOffice.floorplans.map((f) => (
					<DocumentItem
						key={`document-flex-office-${f}`}
						src={f}
						text={t('Planritning')}
					/>
				))}
				{offers?.map((o) => {
					return o.terms_and_conditions
						.map((t) => {
							return { offerName: o.name, doc: t }
						})
						.map((term) => (
							<DocumentItem
								key={`document-flex-office-${term.doc}`}
								src={term.doc}
								text={`${t('Villkor')} ${term.offerName}`}
							/>
						))
				})}
			</Flex>
			<Flex mt={3}>
				{status == 'loading' ? (
					<Skeleton w="100%" h={10} borderRadius="md" />
				) : user && currentOffice ? (
					<RequestShowingButton
						flexoffice={currentOffice}
						variant="outline"
						ml="auto"
						width="min-content"
					/>
				) : (
					<ShowPriceButton
						onClick={onShowPriceModalOpen}
						GA4EventLocation="flex office"
						variant="solid"
						ml="auto"
					>
						{t('Se pris')}
					</ShowPriceButton>
				)}
			</Flex>
		</Box>
	) : null
}
